import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  FullRestaurantData,
  QdmService,
  Restaurant,
  ServiceItem,
} from '../../Services/qdm.service';
import { AuthService, UserChildren } from '../../Services/auth.service';
import {
  Enterprise,
  FullAdmissionsData,
  GestionService,
  OrderHistoryObject,
} from '../../Services/gestion.service';
import { EnterpriseSelectorComponent } from '../../Components/EnterpriseSelector/EnterpriseSelector.component';
import {
  AdmissionSelectorComponent,
  groupAdmissionsByMealMoment,
} from '../../Components/AdmissionSelector/AdmissionSelector.component';
import { DayMenuDisplayComponent } from '../../Components/DayMenuDisplay/DayMenuDisplay.component';
import { RestaurantLoginFormComponent } from '../../Components/RestaurantLoginForm/RestaurantLoginForm.component';
import { DateSelectionHeader } from '../../Components/DateSelectionHeader/DateSelectionHeader';
import { ServiceHeader } from '../../Components/ServiceHeaderSelector/ServiceHeader.component';
import { CartItem, CartService } from '../../Cart/CartService';
import { WeekMenuDisplayComponent } from '../../Components/WeekMenuDisplay/WeekMenuDisplay.component';
import { ModalInformationMessage } from '../../Components/Modal/ModalInformationMessage/ModalInformationMessage';
import { ModalService } from '../../Services/modal.service';
import { OrderVisualizerComponent } from '../../Components/OrderVisualizer/OrderVisualizer.component';
import { OrderHistoryService } from '../../Services/order-history.service';
import { RestaurantHistoryManager } from '../../RestaurantHistoryManager/RestaurantHistoryManager';
import { CookieHistoryImpl } from '../../RestaurantHistoryManager/CookieHistoryImpl';

type DisplayMode = 'day' | 'week';

/**
 * Origine des périodicités :
 * - `admission` : dates commandables du moment repas de l'admission sélectionnée
 * - `service` : moment repas sans admission, dates commandables du mealtime du service
 */
type PeriodicitySource = 'admission' | 'service';

interface PeriodicityRequest {
  source: PeriodicitySource;
  enterpriseId: number;
  mealtimeId: number;
}

interface PeriodicityState {
  source: PeriodicitySource;
  mealtimeId: number;
  dates: string[];
}

interface UserDisplayPreferences {
  uppercaseCourses: boolean;
  boldCourses: boolean;
  dyslexie: boolean;
  hiddenServices: number[];
  markedAllergens: number[];
}

interface CartEntry {
  cartItem: CartItem;
  serviceId: number;
  restaurantId: number;
  childId: number;
  date: Date;
}

/** Clé jour (YYYY-MM-DD) d'une date ou d'une date API */
function dayKey(value: string | Date): string {
  return (typeof value === 'string' ? new Date(Date.parse(value)) : value).formatDate('forApi');
}

/** Copie de la date à minuit (heure locale) */
function startOfDay(value: string | Date): Date {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

@Component({
  selector: 'restaurant-selected-page',
  templateUrl: 'RestaurantSelectedMainPage.html',
  styleUrls: ['RestaurantSelectedMainPage.scss'],
  imports: [
    EnterpriseSelectorComponent,
    AdmissionSelectorComponent,
    DayMenuDisplayComponent,
    RestaurantLoginFormComponent,
    DateSelectionHeader,
    ServiceHeader,
    WeekMenuDisplayComponent,
    OrderVisualizerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'relative',
  },
})
export class RestaurantSelectedMainPageComponent {
  private readonly qdm = inject(QdmService);
  private readonly cart = inject(CartService);
  private readonly gestion = inject(GestionService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastrService);
  private readonly auth = inject(AuthService);
  private readonly modal = inject(ModalService);
  private readonly cookie = inject(CookieService);
  private readonly orderHistoryService = inject(OrderHistoryService);
  private readonly restHistory = inject(RestaurantHistoryManager);
  private readonly destroyRef = inject(DestroyRef);

  // ---------------------------------------------------------------------------
  // État partagé (source unique : les services)
  // ---------------------------------------------------------------------------

  protected readonly restaurant = this.qdm.selectedRestaurant;
  protected readonly user = this.auth.user;
  protected readonly isLoggedIn = this.auth.isLoggedIn;
  protected readonly selectedMomentRepas = this.qdm.selectedEnterprise;
  protected readonly selectedAdmission = this.qdm.selectedAdmission;
  protected readonly selectedService = this.qdm.currentService;
  protected readonly selectedDate = this.qdm.selectedDate;
  private readonly selectedChild = this.qdm.selectedChild;

  // ---------------------------------------------------------------------------
  // État local
  // ---------------------------------------------------------------------------

  protected readonly allMenus = signal<ServiceItem[] | undefined>(undefined);
  private readonly restaurantData = signal<FullRestaurantData | undefined>(undefined);
  protected readonly admissions = signal<FullAdmissionsData[]>([]);
  protected readonly areAdmissionsLoaded = signal(false);
  private readonly periodicityState = signal<PeriodicityState | undefined>(undefined);
  private readonly commandsHistory = signal<OrderHistoryObject[]>([]);
  private readonly companyId = signal(-1);

  /** Moment repas pour lequel la 1ère date commandable (mode sans admission) a déjà été choisie */
  private servicePeriodicityInitializedFor: number | undefined = undefined;

  // ---------------------------------------------------------------------------
  // État dérivé
  // ---------------------------------------------------------------------------

  protected readonly mode = computed<DisplayMode>(() =>
    this.restaurant()?.is_use_password ? 'day' : 'week'
  );

  protected readonly periodicities = computed(() => this.periodicityState()?.dates);

  protected readonly dayConsultationLimit = computed(() => {
    const data = this.restaurantData();
    return data ? { past: data.pub_past, future: data.pub_future } : { future: 30, past: 7 };
  });

  private readonly allServices = computed(() => this.restaurantData()?.services ?? []);

  protected readonly isAcceptPayment = computed(
    () => this.selectedMomentRepas()?.isAcceptPayment ?? false
  );

  /** Moments repas activés du restaurant. `undefined` tant que les données entreprise ne sont pas chargées. */
  protected readonly momentsRepas = computed<Enterprise[] | undefined>(() => {
    const enterpriseData = this.auth.userEnterprise();
    const restaurant = this.restaurant();
    if (!enterpriseData || !restaurant) return undefined;

    return enterpriseData.enterprises.filter(
      (e) => parseInt(e.companies[0]?.reference) === restaurant.id && e.isEnabled
    );
  });

  private readonly preferences = computed<UserDisplayPreferences>(() => {
    // Dépendance volontaire : relit le cookie quand les préférences (non connecté) changent
    this.qdm.preferences();
    const pref = this.readRawPreferences();

    const childPref = pref[`child-${this.selectedChild()?.id || -1}`] || {};
    const restoPref = childPref[`resto-${this.restaurant()?.id}`] || {};
    const accessibility = childPref.accessibility;

    return {
      uppercaseCourses: accessibility?.uppercasecourses ?? false,
      boldCourses: accessibility?.['bold-courses'] ?? false,
      dyslexie: accessibility?.dyslexie ?? false,
      hiddenServices: restoPref['hidden-services'] || [],
      markedAllergens: restoPref['marked-allergens'] || [],
    };
  });

  protected readonly uppercaseCourses = computed(() => this.preferences().uppercaseCourses);
  protected readonly boldCourses = computed(() => this.preferences().boldCourses);
  protected readonly dyslexie = computed(() => this.preferences().dyslexie);
  protected readonly hiddenServices = computed(() => this.preferences().hiddenServices);
  protected readonly markedAllergens = computed(() => this.preferences().markedAllergens);

  protected readonly showWeekEnd = computed(() =>
    (this.allMenus() ?? []).some((m) => {
      const day = new Date(Date.parse(m.day_start)).getDay();
      return day === 0 || day === 6;
    })
  );

  /** Jours avec menus ; restreints aux périodicités quand elles viennent d'une admission */
  protected readonly daysWithMenus = computed<Date[]>(() => {
    const menus = this.allMenus() ?? [];
    const periodicity = this.periodicityState();

    if (this.mode() !== 'day' || periodicity?.source !== 'admission') {
      return menus.map((m) => new Date(Date.parse(m.day_start)));
    }

    const periodicityTimes = new Set(periodicity.dates.map((d) => startOfDay(d).getTime()));
    return menus
      .map((m) => startOfDay(m.day_start))
      .filter((d) => periodicityTimes.has(d.getTime()));
  });

  /** Jours ayant une commande pour l'enfant sélectionné (ou sans enfant) */
  protected readonly daysWithOrders = computed<Date[]>(() => {
    if (!this.user()) return [];

    const childId = this.selectedChild()?.id || -1;
    return this.commandsHistory()
      .filter((h) => (childId !== -1 ? h.child_id === childId : h.child_id === null))
      .map((h) => startOfDay(h.deliveryDate));
  });

  /** Jours commandés partiellement / entièrement (tous les services du jour commandés) */
  private readonly orderCompletionByDay = computed(() => {
    const partial: Date[] = [];
    const full: Date[] = [];
    const menus = this.allMenus();
    if (!menus) return { partial, full };

    const momentId = this.selectedMomentRepas()?.id;
    const acceptPayment = this.isAcceptPayment();

    const ordersByDay = new Map<string, OrderHistoryObject[]>();
    this.commandsHistory()
      .filter((h) => h.status === 'valid')
      .filter((h) => !acceptPayment || h.isOrderForGroup || h.isPaid)
      .filter((h) => h.enterprisePlace?.id === momentId)
      .forEach((h) => {
        const key = startOfDay(h.deliveryDate).formatDate('forApi');
        ordersByDay.set(key, [...(ordersByDay.get(key) ?? []), h]);
      });

    ordersByDay.forEach((orders, key) => {
      const dayTime = startOfDay(key).getTime();
      const servicesOfDay = menus
        .filter((m) => startOfDay(m.day_start).getTime() === dayTime)
        .map((m) => m.svc_id);
      const orderedServices = orders.map((h) => h.svc_id).filter((id) => id !== null);

      const isFull = servicesOfDay.every((id) => orderedServices.includes(id));
      (isFull ? full : partial).push(new Date(key));
    });

    return { partial, full };
  });

  protected readonly daysWithPartialsOrders = computed(() => this.orderCompletionByDay().partial);
  protected readonly daysWithFullOrders = computed(() => this.orderCompletionByDay().full);

  /** Commandes valides du jour sélectionné, groupées par svc_id */
  protected readonly ordersForThisDayAndService = computed(() => {
    const result: Record<number, OrderHistoryObject[]> = {};
    const date = this.selectedDate();
    if (!date) return result;

    const key = date.formatDate('forApi');
    const child = this.selectedChild();
    const companyId = this.companyId();

    for (const order of this.commandsHistory()) {
      if (order.status !== 'valid' || order.svc_id === null) continue;
      if (dayKey(order.deliveryDate) !== key) continue;
      if (child && order.child_id !== child.id) continue;
      if (order.company?.id !== companyId) continue;

      (result[order.svc_id] ??= []).push(order);
    }
    return result;
  });

  protected readonly servicesWithOrdersOnIt = computed(() =>
    Object.keys(this.ordersForThisDayAndService()).map(Number)
  );

  protected readonly selectedServiceOrders = computed(() => {
    const service = this.selectedService();
    return service ? this.ordersForThisDayAndService()[service.svc_id] : undefined;
  });

  /** Commandes du premier service commandé (affichées quand aucun service n'est sélectionné) */
  protected readonly firstOrders = computed(
    () => Object.values(this.ordersForThisDayAndService())[0] ?? []
  );

  /** Services affichables pour la date sélectionnée */
  protected readonly services = computed<ServiceItem[]>(() => {
    const menus = this.allMenus();
    const date = this.selectedDate();
    if (!menus || !date) return [];

    let services =
      this.mode() === 'day'
        ? menus.filter((menu) => menu.day_start === date.formatDate('forApi'))
        : this.uniqueServicesOfWeek(menus, date);

    const child = this.selectedChild();
    if (child) {
      services = this.filterServicesForChild(services, child);
    }

    // Aucun menu mais des commandes ce jour : services construits depuis les commandes
    if (services.length === 0) {
      return this.servicesFromOrders(date, child);
    }

    const admissions = this.admissions();
    if (admissions.length > 0) {
      const mealtimes = admissions.map((a) => a.mealMoment?.qdm_id);
      services = services.filter((s) => mealtimes.includes(s.mealtime_id || -1));
    }

    return services;
  });

  /**
   * Service qui devrait être sélectionné :
   * - jamais un service masqué
   * - avec une admission : le service du mealtime de l'admission (aucun s'il est absent ce jour)
   * - sinon : le même svc_id que le service courant, à défaut le premier
   */
  private readonly targetService = computed<ServiceItem | undefined>(() => {
    const hidden = this.hiddenServices();
    const available = this.services().filter((s) => !hidden.includes(s.svc_id));
    if (available.length === 0) return undefined;

    const current = this.selectedService();
    const sameService = current ? available.find((s) => s.svc_id === current.svc_id) : undefined;

    const admission = this.selectedAdmission();
    if (admission && this.mode() === 'day') {
      const mealtimeId = Number(admission.mealMoment.qdm_id);
      if (sameService && sameService.mealtime_id === mealtimeId) return sameService;
      const admissionService = available.find((s) => s.mealtime_id === mealtimeId);
      if (admissionService) return admissionService;
      // Jour sans menu mais commandé : services construits depuis les commandes (sans mealtime)
      if (available.every((s) => s.mealtime_id === null)) return sameService ?? available[0];
      return undefined;
    }

    return sameService ?? available[0];
  });

  /**
   * Admission à sélectionner automatiquement : la seule disponible, ou la seule de l'onglet
   * (moment repas du service courant, sinon le premier). Calculé avant l'affichage du sélecteur
   * pour qu'il n'apparaisse pas puis disparaisse.
   */
  private readonly autoSelectableAdmission = computed<FullAdmissionsData | undefined>(() => {
    if (!this.selectedMomentRepas() || this.selectedAdmission() || !this.areAdmissionsLoaded()) {
      return undefined;
    }

    const admissions = this.admissions();
    if (admissions.length === 1) return admissions[0];

    const tabs = groupAdmissionsByMealMoment(admissions);
    if (tabs.length === 0) return undefined;
    // Cas d'erreur géré par le sélecteur (redirection no-mealmoments)
    if (tabs.length === 1 && tabs[0].realId == 0) return undefined;

    const mealtimeId = this.selectedService()?.mealtime_id;
    const tab = tabs.find((t) => String(t.id) === String(mealtimeId)) ?? tabs[0];
    return tab.admissions.length === 1 ? tab.admissions[0] : undefined;
  });

  protected readonly showAdmissionSelector = computed(
    () =>
      !!this.selectedMomentRepas() &&
      !this.selectedAdmission() &&
      this.areAdmissionsLoaded() &&
      this.admissions().length > 1 &&
      !this.autoSelectableAdmission()
  );

  /**
   * Contexte de commande résolu : admissions chargées et, s'il y en a, admission choisie
   * avec ses périodicités reçues (la date est alors déjà positionnée).
   * Évite d'afficher le menu avant le sélecteur d'admission ou avant le saut de date.
   */
  protected readonly isOrderContextReady = computed(() => {
    if (!this.areAdmissionsLoaded()) return false;
    if (this.admissions().length === 0) return true;

    const admission = this.selectedAdmission();
    if (!admission) return false;

    const periodicity = this.periodicityState();
    return (
      periodicity?.source === 'admission' &&
      periodicity.mealtimeId === admission.mealMoment?.qdm_id
    );
  });

  protected readonly isLoading = computed(() => {
    if (this.allMenus() === undefined) return true;
    if (this.mode() !== 'day') return false;
    // Connecté (tokens) mais utilisateur / entreprise pas encore chargés
    if (this.isLoggedIn() && (!this.user() || this.momentsRepas() === undefined)) return true;
    if (!this.user() || !this.selectedMomentRepas()) return false;
    return !this.isOrderContextReady() && !this.showAdmissionSelector();
  });

  /** Sélecteur de moment repas : masqué tant que les données ne sont pas chargées ou s'il n'y a qu'un choix */
  protected readonly showEnterpriseSelector = computed(() => {
    const moments = this.momentsRepas();
    return (
      this.mode() === 'day' &&
      !!this.user() &&
      !this.selectedMomentRepas() &&
      moments !== undefined &&
      moments.length !== 1
    );
  });

  /** Dernier mealtime réel sélectionné (ignore les services sans mealtime construits depuis les commandes) */
  private readonly lastServiceMealtimeId = linkedSignal<ServiceItem | undefined, number | undefined>({
    source: this.selectedService,
    computation: (service, previous) => service?.mealtime_id ?? previous?.value,
  });

  private readonly periodicityRequest = computed<PeriodicityRequest | null>(
    () => {
      if (this.mode() !== 'day' || this.allMenus() === undefined) return null;

      const moment = this.selectedMomentRepas();
      if (!moment) return null;

      const admission = this.selectedAdmission();
      if (admission) {
        const mealtimeId = admission.mealMoment?.qdm_id;
        return mealtimeId ? { source: 'admission', enterpriseId: moment.id, mealtimeId } : null;
      }

      if (
        moment.admissions.length === 0 &&
        this.areAdmissionsLoaded() &&
        this.admissions().length === 0
      ) {
        const mealtimeId = this.lastServiceMealtimeId() ?? this.allMenus()?.[0]?.mealtime_id;
        return mealtimeId ? { source: 'service', enterpriseId: moment.id, mealtimeId } : null;
      }

      return null;
    },
    {
      equal: (a, b) =>
        a?.source === b?.source &&
        a?.enterpriseId === b?.enterpriseId &&
        a?.mealtimeId === b?.mealtimeId,
    }
  );

  /** Pré-remplissage du panier (autoFillFirstChoice) : relancé quand l'un de ces éléments change */
  private readonly autoFillKey = computed(() => {
    const admission = this.selectedAdmission();
    const service = this.selectedService();
    const date = this.selectedDate();
    if (!admission || !service || !date || !this.selectedMomentRepas()?.autoFillFirstChoice) {
      return null;
    }
    return [
      admission.admissionId,
      service.id,
      date.formatDate('forApi'),
      this.selectedChild()?.id ?? -1,
      this.qdm.groupOrderQuantity(),
    ].join('|');
  });

  constructor() {
    (window as any).dumpAll = () => this.dumpAll();
    this.destroyRef.onDestroy(() => delete (window as any).dumpAll);

    // L'ordre compte : le reset du restaurant (synchrone) précède les autres abonnements
    this.listenRestaurant();
    this.listenAuthorization();
    this.listenCommandsHistory();
    this.listenMomentRepas();
    this.listenPeriodicities();

    // Garde le service sélectionné cohérent avec la liste des services affichables
    effect(() => {
      const target = this.targetService();
      if (target !== this.selectedService()) {
        untracked(() => this.qdm.currentServiceSubject.next(target));
      }
    });

    // Un seul moment repas : sélection automatique
    effect(() => {
      const moments = this.momentsRepas();
      if (
        this.mode() === 'day' &&
        this.user() &&
        !this.selectedMomentRepas() &&
        moments?.length === 1
      ) {
        untracked(() => this.momentRepasChanged(moments[0]));
      }
    });

    effect(() => {
      const admission = this.autoSelectableAdmission();
      if (admission) {
        untracked(() => this.admissionSelectionChanged(admission));
      }
    });

    effect(() => {
      if (this.autoFillKey()) {
        untracked(() => this.addMandatoryElementsToCart());
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Flux de chargement
  // ---------------------------------------------------------------------------

  /** Changement de restaurant : réinitialisation puis chargement des menus et infos restaurant */
  private listenRestaurant() {
    this.qdm.selectedRestaurant$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        tap((restaurant) => this.onRestaurantChanged(restaurant)),
        switchMap((restaurant) =>
          restaurant
            ? from(this.qdm.fetchMenus(restaurant.cstID, restaurant.id)).pipe(
                catchError(() => of([] as ServiceItem[]))
              )
            : of(undefined)
        ),
        takeUntilDestroyed()
      )
      .subscribe((menus) => {
        this.allMenus.set(menus);

        if (menus && this.mode() === 'week') {
          this.selectDate(menus.length > 0 ? new Date(Date.parse(menus[0].day_start)) : new Date());
        }
      });
  }

  private onRestaurantChanged(restaurant: Restaurant | undefined) {
    if (!restaurant) {
      this.toast.error("Vous n'avez pas sélectionné de restaurant, redirection vers la liste");
      this.router.navigate(['']);
      this.qdm.allergensSubject.next([]);
      return;
    }

    // Réinitialiser les données du restaurant précédent
    this.qdm.selectEnterprise(undefined);
    this.admissionSelectionChanged(undefined);
    this.groupOrderSelectionQuantityChanged(1);

    this.allMenus.set(undefined);
    this.restaurantData.set(undefined);
    this.admissions.set([]);
    this.areAdmissionsLoaded.set(false);
    this.periodicityState.set(undefined);
    this.companyId.set(-1);
    this.servicePeriodicityInitializedFor = undefined;

    this.selectDate(new Date());

    this.qdm.fetchRestaurantById(restaurant.id).then((data) => {
      if (this.restaurant()?.id !== restaurant.id) return;

      this.restaurantData.set(data);
      const message = (data as any).message;
      if (message && message !== '') {
        this.showRestaurantMessage(message);
      }
    });
  }

  /** Restaurant à accès restreint : vérification d'accès et récupération du companyId */
  private listenAuthorization() {
    combineLatest([this.qdm.selectedRestaurant$, this.auth.user$])
      .pipe(
        distinctUntilChanged(([r1, u1], [r2, u2]) => r1?.id === r2?.id && u1?.id === u2?.id),
        takeUntilDestroyed()
      )
      .subscribe(([restaurant, user]) => {
        if (!restaurant?.is_use_password || !user) return;

        this.auth
          .isUserAuthorizedToRestaurant(restaurant.id, user)
          .then((isAuthorized) => {
            if (isAuthorized) return;
            this.router
              .navigate(['/restaurant', restaurant.id, 'not-authorized'], {
                relativeTo: this.router.routerState.root,
              })
              .then(() => {
                this.restHistory.removeRestaurantById(new CookieHistoryImpl(), restaurant.id);
              });
          })
          .catch(() => {});

        this.gestion
          .fetchCompanyId(restaurant.id, restaurant.cstID)
          .then((companyId) => {
            if (this.restaurant()?.id === restaurant.id) {
              this.companyId.set(companyId);
            }
          })
          .catch(() => {});
      });
  }

  /** Historique de commandes : rechargé (sans cache) à l'arrivée sur la page et à chaque demande de rafraîchissement */
  private listenCommandsHistory() {
    combineLatest([
      this.auth.userEnterprise$.pipe(
        map((enterprise) => enterprise?.id),
        distinctUntilChanged()
      ),
      this.orderHistoryService.orderHistorySubject,
    ])
      .pipe(
        switchMap(([companyId]) =>
          companyId === undefined
            ? of([] as OrderHistoryObject[])
            : from(this.gestion.fetchCommandsHistory(companyId, true)).pipe(
                catchError(() => of([] as OrderHistoryObject[]))
              )
        ),
        takeUntilDestroyed()
      )
      .subscribe((orders) => this.commandsHistory.set(orders));
  }

  /** Changement de moment repas : contrôle de périodicité du jour puis chargement des admissions */
  private listenMomentRepas() {
    // 1. Réinitialisation (abonné en premier : passe avant le chargement ci-dessous)
    this.qdm.selectedEnterprise$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        takeUntilDestroyed()
      )
      .subscribe((moment) => {
        this.admissions.set([]);
        this.areAdmissionsLoaded.set(false);
        this.servicePeriodicityInitializedFor = undefined;

        if (moment && !this.hasPeriodicityForToday(moment)) {
          this.noPeriodicities();
          return;
        }

        // Nouveau moment repas : l'admission doit être re-choisie
        this.admissionSelectionChanged(undefined);
      });

    // 2. Chargement des admissions de l'utilisateur pour ce moment repas
    combineLatest([this.qdm.selectedEnterprise$, this.auth.user$])
      .pipe(
        distinctUntilChanged(([m1, u1], [m2, u2]) => m1?.id === m2?.id && u1?.id === u2?.id),
        switchMap(([moment, user]) =>
          moment && user && this.hasPeriodicityForToday(moment)
            ? from(this.gestion.fetchAllAdmissions(user.id, moment.id)).pipe(
                catchError(() => of([] as FullAdmissionsData[]))
              )
            : of(null)
        ),
        takeUntilDestroyed()
      )
      .subscribe((admissions) => {
        if (admissions === null) return;
        this.admissions.set(admissions);
        this.areAdmissionsLoaded.set(true);
      });
  }

  /** Chargement des dates commandables, puis sélection de la première date valide */
  private listenPeriodicities() {
    toObservable(this.periodicityRequest)
      .pipe(
        switchMap((request) =>
          request
            ? from(
                this.gestion.getOrderableDates('', '', request.enterpriseId, request.mealtimeId)
              ).pipe(
                map((dates) => ({ request, dates })),
                catchError(() => of({ request, dates: [] as string[] }))
              )
            : of(null)
        ),
        takeUntilDestroyed()
      )
      .subscribe((result) => {
        if (!result) {
          this.periodicityState.set(undefined);
          return;
        }

        this.periodicityState.set({
          source: result.request.source,
          mealtimeId: result.request.mealtimeId,
          dates: result.dates,
        });
        this.selectFirstOrderableDate(result.request, result.dates);
      });
  }

  private selectFirstOrderableDate(request: PeriodicityRequest, dates: string[]) {
    const periodicityDates = dates.map((d) => startOfDay(d));
    const firstDate = this.extractFirstDateAfterToday(periodicityDates);

    if (request.source === 'admission') {
      if (firstDate) {
        this.selectDate(firstDate);
      } else {
        this.noMenus();
      }
      return;
    }

    if (!firstDate) {
      this.noPeriodicities();
      return;
    }

    // Première arrivée sur ce moment repas : premier jour avec menu dans les périodicités
    if (this.servicePeriodicityInitializedFor !== request.enterpriseId) {
      this.servicePeriodicityInitializedFor = request.enterpriseId;
      const firstMenuDay = (this.allMenus() ?? [])
        .map((m) => dayKey(m.day_start))
        .find((day) => dates.includes(day));
      this.selectDate(firstMenuDay ? new Date(Date.parse(firstMenuDay)) : firstDate);
      return;
    }

    const current = this.selectedDate();
    if (!current || startOfDay(current).getTime() !== firstDate.getTime()) {
      this.selectDate(firstDate);
    }
  }

  // ---------------------------------------------------------------------------
  // Actions (template)
  // ---------------------------------------------------------------------------

  momentRepasChanged(enterprise: Enterprise | undefined) {
    this.qdm.selectEnterprise(enterprise);
  }

  groupOrderSelectionQuantityChanged(groupOrderQty: number) {
    this.qdm.groupOrderQuantitySubject.next(groupOrderQty);
  }

  admissionSelectionChanged(admission: FullAdmissionsData | undefined) {
    this.cart.deleteAllCarts();
    this.qdm.selectedAdmissionSubject.next(admission ?? null);
  }

  selectServiceChanged(service: ServiceItem) {
    if (this.hiddenServices().includes(service.svc_id)) return;

    this.qdm.currentServiceSubject.next(service);

    // L'admission est liée à un moment repas : changer de mealtime impose de la re-choisir
    const admission = this.selectedAdmission();
    if (
      admission &&
      service.mealtime_id !== null &&
      service.mealtime_id !== Number(admission.mealMoment.qdm_id)
    ) {
      this.qdm.selectedAdmissionSubject.next(null);
    }
  }

  dateChangedExact(date: Date) {
    this.selectDate(date);
  }

  /**
   * @param delta Valeurs possibles : [-1, +1, -7, +7]
   */
  dateChangedWithArrows(delta: number) {
    const selectedDate = this.selectedDate();
    if (!selectedDate) return;

    if (this.mode() === 'week') {
      // Changer de semaine uniquement si la semaine cible contient des menus
      const nextDate = new Date(selectedDate);
      nextDate.setDate(nextDate.getDate() + delta);
      const hasMenusThisWeek = (this.allMenus() ?? []).some(
        (m) => new Date(Date.parse(m.day_start)).getWeekNumber() === nextDate.getWeekNumber()
      );
      if (hasMenusThisWeek) {
        this.selectDate(nextDate);
        return;
      }
    }

    // Uniquement les jours à la fois dans les périodicités ET avec des menus
    const menuTimes = new Set(this.daysWithMenus().map((d) => startOfDay(d).getTime()));
    const orderableDates = (this.periodicities() ?? [])
      .map((d) => startOfDay(d))
      .filter((d) => menuTimes.has(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());

    if (orderableDates.length === 0) return;

    const currentTime = startOfDay(selectedDate).getTime();
    const currentIndex = orderableDates.findIndex((d) => d.getTime() === currentTime);

    if (currentIndex === -1) {
      const closestDate = this.extractFirstDateAfterToday(orderableDates);
      if (closestDate) {
        this.selectDate(closestDate);
      }
      return;
    }

    // Navigation circulaire
    const step = delta > 0 ? 1 : -1;
    const newIndex = (currentIndex + step + orderableDates.length) % orderableDates.length;
    this.selectDate(orderableDates[newIndex]);
  }

  // ---------------------------------------------------------------------------
  // Règles métier
  // ---------------------------------------------------------------------------

  /** Le moment repas a-t-il une périodicité pour le jour de la semaine courant ? */
  private hasPeriodicityForToday(moment: Enterprise) {
    const today = new Date().getDay();
    return (moment.periodicities ?? []).some((p) => p.dayCode === today);
  }

  /** Services dont le nom contient un des profils alimentaires de l'enfant */
  private filterServicesForChild(services: ServiceItem[], child: UserChildren) {
    const foodProfilesCodes = child.foodProfiles.map((fp) =>
      fp.code.replaceAll('_', ' ').toLowerCase()
    );
    return services.filter((service) =>
      foodProfilesCodes.some((code) => service.name.toLowerCase().includes(code))
    );
  }

  /** Mode semaine : services de la semaine dédupliqués par svc_id (composantes dédupliquées par id) */
  private uniqueServicesOfWeek(menus: ServiceItem[], date: Date): ServiceItem[] {
    const week = date.getWeekNumber();
    const uniqueServices = new Map<number, ServiceItem>();

    menus
      .filter((menu) => new Date(Date.parse(menu.day_start)).getWeekNumber() === week)
      .forEach((service) => {
        if (uniqueServices.has(service.svc_id)) return;

        const uniqueComponents = new Map<number, ServiceItem['components'][number]>();
        service.components.forEach((component) => {
          if (!uniqueComponents.has(component.id)) {
            uniqueComponents.set(component.id, component);
          }
        });
        uniqueServices.set(service.svc_id, {
          ...service,
          components: Array.from(uniqueComponents.values()),
        });
      });

    return Array.from(uniqueServices.values());
  }

  /** Services minimaux construits depuis les commandes du jour (jour sans menu mais commandé) */
  private servicesFromOrders(date: Date, child: UserChildren | null): ServiceItem[] {
    const key = date.formatDate('forApi');
    const allServices = this.allServices();
    const services: ServiceItem[] = [];

    this.commandsHistory()
      .filter((h) => dayKey(h.deliveryDate) === key)
      .filter((h) => !child || h.child_id === child.id)
      .forEach((order) => {
        if (order.svc_id === null || services.some((s) => s.svc_id === order.svc_id)) return;

        const service = allServices.find((s) => s.id === order.svc_id);
        if (!service) return;

        services.push({
          id: order.id,
          svc_id: order.svc_id,
          name: service.name,
          day_start: order.deliveryDate,
          day_end: order.deliveryDate,
          mealtime_id: null,
          rst_id: 0,
          svt_id: 0,
          cst_id: 0,
          label: `Service ${order.svc_id}`,
          components: [],
          courses: [],
          description: '',
          imageId: null,
        } as unknown as ServiceItem);
      });

    return services;
  }

  /**
   * Première date commandable :
   * - la date sélectionnée si elle est dans les périodicités et a un menu
   * - sinon la première date >= aujourd'hui, dans les périodicités, sans commande et avec un menu
   */
  private extractFirstDateAfterToday(periodicities: Date[]): Date | undefined {
    const menus = this.allMenus();
    const menuTimes = menus
      ? new Set(menus.map((m) => startOfDay(m.day_start).getTime()))
      : undefined;
    const hasMenu = (date: Date) => !menuTimes || menuTimes.has(startOfDay(date).getTime());

    const selectedDate = this.selectedDate();
    if (selectedDate) {
      const selectedTime = startOfDay(selectedDate).getTime();
      const isInPeriodicities = periodicities.some((d) => startOfDay(d).getTime() === selectedTime);
      if (isInPeriodicities && hasMenu(selectedDate)) {
        return selectedDate;
      }
    }

    const todayTime = startOfDay(new Date()).getTime();
    const orderedTimes = new Set(this.daysWithOrders().map((d) => d.getTime()));

    return [...periodicities]
      .sort((a, b) => a.getTime() - b.getTime())
      .filter((d) => !orderedTimes.has(d.getTime()))
      .filter(hasMenu)
      .find((d) => d.getTime() >= todayTime);
  }

  /** Pré-remplit le panier avec les choix imposés par l'admission (autoFillFirstChoice) */
  private addMandatoryElementsToCart() {
    const selectedAdmission = this.selectedAdmission();
    const date = this.selectedDate();
    const service = this.selectedService();
    const restaurant = this.restaurant();
    if (!selectedAdmission || !date || !service || !restaurant) return;

    const child = this.selectedChild();
    this.cart.clearCart(date, child || undefined, service, restaurant);

    const admission = this.admissions().find(
      (a) => a.admissionId === selectedAdmission.admissionId
    );
    if (!admission) return;

    const toCartEntry = (cartItem: CartItem): CartEntry => ({
      cartItem,
      serviceId: service.id,
      restaurantId: restaurant.id,
      childId: child?.id || -1,
      date,
    });

    const itemsToAdd: CartEntry[] = [];

    if (admission.isFullMenu) {
      service.components.forEach((family) => {
        family.courses.forEach((course) => {
          itemsToAdd.push(toCartEntry(this.cart.toCartItem(course, family)));
        });
      });
    } else {
      admission.admissionParts.forEach((part) => {
        if (itemsToAdd.length >= admission.admissionMax) return;

        const family = service.components.find((c) => c.family_id === part.serviceId);
        if (family?.courses.length === 1) {
          itemsToAdd.push(toCartEntry(this.cart.toCartItem(family.courses[0], family)));
        }
      });
    }

    // Ajout groupé pour éviter les race conditions
    if (itemsToAdd.length > 0) {
      this.cart.addMultipleItemsToCart(itemsToAdd, this.qdm.groupOrderQuantity());
    }
  }

  // ---------------------------------------------------------------------------
  // Utilitaires
  // ---------------------------------------------------------------------------

  private selectDate(date: Date) {
    this.qdm.selectedDateSubject.next(date);
  }

  private readRawPreferences(): any {
    try {
      const user = this.user();
      if (user) {
        return JSON.parse(JSON.parse(user.preferences || '{}').preferences || '{}');
      }
      const cookiePref = this.cookie.check('preferences')
        ? JSON.parse(this.cookie.get('preferences'))
        : { preferences: '{}' };
      return JSON.parse(cookiePref.preferences || '{}');
    } catch {
      return {};
    }
  }

  private showRestaurantMessage(message: string) {
    this.modal.open(ModalInformationMessage, { message }, () => {});
  }

  private noPeriodicities() {
    this.router.navigate(['/error/order-not-available'], { state: { cause: 'periodicities' } });
  }

  private noMenus() {
    this.router.navigate(['/error/order-not-available'], { state: { cause: 'menus' } });
  }

  /**
   * Dump des variables importantes du composant.
   * Accessible via window.dumpAll()
   */
  dumpAll() {
    const dump = {
      user: this.user(),
      restaurant: this.restaurant(),
      mode: this.mode(),
      customer: this.qdm.currentCustomer(),
      companyId: this.companyId(),

      allMenus: this.allMenus(),
      services: this.services(),
      selectedService: this.selectedService(),
      allServices: this.allServices(),

      selectedDate: this.selectedDate(),
      periodicities: this.periodicityState(),
      daysWithMenus: this.daysWithMenus(),
      daysWithOrders: this.daysWithOrders(),
      daysWithPartialsOrders: this.daysWithPartialsOrders(),
      daysWithFullOrders: this.daysWithFullOrders(),
      dayConsultationLimit: this.dayConsultationLimit(),

      momentsRepas: this.momentsRepas(),
      selectedMomentRepas: this.selectedMomentRepas(),
      admissions: this.admissions(),
      selectedAdmission: this.selectedAdmission(),
      areAdmissionsLoaded: this.areAdmissionsLoaded(),

      selectedChild: this.selectedChild(),
      commandsHistory: this.commandsHistory(),
      ordersForThisDayAndService: this.ordersForThisDayAndService(),
      servicesWithOrdersOnIt: this.servicesWithOrdersOnIt(),

      preferences: this.preferences(),
      isAcceptPayment: this.isAcceptPayment(),
      showWeekEnd: this.showWeekEnd(),
    };

    console.log('🔍 DUMP ALL VARIABLES - RestaurantSelectedMainPage', dump);
    return dump;
  }
}
